/**
 * The client mirror of `evaluateAccess()` (`contracts.md` §10), ported from
 * `prototype/screens/shared.js`'s `accessBlock()` — presentation only, per PRD §6: it renders
 * the reason the server would give so a card can grey and an actuator can disable before a
 * round trip. **It never grants, and cached client state is never evidence** — the open call's
 * own `403`/`409` is the authority, and every actuator in this PRD renders whatever `DenyReason`
 * that call actually returns, in addition to (never instead of) this proactive read.
 *
 * Two clauses are unreachable here, deliberately:
 *
 *  - **`subscription-expired` is only ever computable on Gate Detail's own manager view.**
 *    `CachedGate` (Home's allow-list, `device-cache/snapshot.ts`) and `GateDetail` for a
 *    resident (`GateProjection`, P3) both omit the `subscription` block outright — D99/§7.10:
 *    residents never see billing, on any surface. A resident's stale local belief is therefore
 *    never "not expired" *or* "expired"; it is simply absent, and the open call's own refusal is
 *    what tells them.
 *  - **`transport-not-permitted` (a BLE-only grant out of range) has no client-side signal in
 *    the MVP.** Resident BLE and gate proximity are D94/P16, not built. §13 "cloud open" is explicit that
 *    this PRD asserts "transport is shown", not BLE distance logic.
 */

import type { DenyReason } from '@selogate/core'
import type { CachedGate } from '../../device-cache/snapshot'
import type { GateDetail } from './api'

export interface CardAccess {
  readonly reason: DenyReason | null
  readonly latched: boolean
}

/**
 * Home's tile. Only the two clauses `CachedGate`'s allow-list can answer.
 *
 * **D168: the latch reads the gate's state and no role.** The `viewerRole === 'manager' ? null`
 * exemption D40 put here is gone from both overloads, mirroring `evaluateAccess()`'s clause 3 —
 * this function's whole job is to render the reason the server would give, so an exemption here
 * that the server no longer honours renders an enabled control over a refusal.
 */
export function accessForCard(gate: CachedGate): CardAccess {
  if (gate.viewerStatus === 'expired') return { reason: 'grant-expired', latched: false }
  const latched = gate.operatingState !== 'normal'
  if (latched) return { reason: 'gate-latched', latched: true }
  return { reason: null, latched }
}

/**
 * Gate Detail's actuator. Adds `subscription-expired` when the read carried one (manager-only).
 *
 * The latch clause is role-free here for the same D168 reason as `accessForCard` above — and this
 * is the overload where the old exemption was visible as a defect: the screen's refusal block keys
 * off `latched` while its Open/Pedestrian buttons key off `reason`, so a manager on a latched gate
 * read *control disabled — gate is latched* above two enabled buttons.
 */
export function accessForDetail(gate: GateDetail): CardAccess {
  if (gate.subscription !== undefined && isSubscriptionExpired(gate.subscription.renewsAt)) {
    return { reason: 'subscription-expired', latched: false }
  }
  if (gate.viewer.status === 'expired') return { reason: 'grant-expired', latched: false }
  const latched = gate.operatingState !== 'normal'
  if (latched) return { reason: 'gate-latched', latched: true }
  return { reason: null, latched }
}

export function isSubscriptionExpired(renewsAt: string): boolean {
  const at = new Date(renewsAt).getTime()
  return Number.isFinite(at) && at < Date.now()
}

/** Days until `renewsAt`, floored at 0. Negative/invalid input reads as expired (`null`). */
export function renewalDaysRemaining(renewsAt: string): number | null {
  if (isSubscriptionExpired(renewsAt)) return null
  const ms = new Date(renewsAt).getTime() - Date.now()
  return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)))
}

/**
 * 🔸 The threshold a gate's subscription counts as "running low" — shared by Gate Detail's
 * colored days-remaining badge (B2) and Home's red-tile rule (C3), so the two can never disagree
 * about what "low" means. Ported from the prototype's `shared.js` `SUBSCRIPTION_LOW_DAYS`.
 */
export const SUBSCRIPTION_LOW_DAYS = 50

const TYPE_ICON = {
  barrier: 'house',
  door: 'door-open',
  parking: 'square-parking',
  garage: 'car',
} as const

/** The card's default icon when no personalization overrides it — ported 1:1 from `shared.js`. */
export function iconForGateType(type: string): 'house' | 'door-open' | 'square-parking' | 'car' {
  return (
    (TYPE_ICON as Readonly<Record<string, 'house' | 'door-open' | 'square-parking' | 'car'>>)[type] ?? 'house'
  )
}

// `VIEW_ICONS`/`VIEW_COLORS` (P5's personalization wire vocabulary) and their client-side
// rendering moved to `components/icons/viewVocabulary.ts` — a P6-owned module — by T6
// (`docs/engineering/review/03-cleanup-plan.md`; see `docs/product/prd-split-seams.md`'s web
// ownership map). Import them from there.
